// doctor-frontend/src/clinical/visitSectionModules.js
// Reusable clinical modules are activated by complaints/pathways.
// They are NOT separate competing pathways. This file controls presentation
// priority only; it must not auto-order tests or auto-prescribe treatment.

export const CLINICAL_MODULE_LABELS = Object.freeze({
  'fever-infection': 'Fever / infection',
  respiratory: 'Respiratory',
  cardiovascular: 'Cardiovascular',
  gastrointestinal: 'Gastrointestinal',
  neurological: 'Neurological',
  musculoskeletal: 'Musculoskeletal',
  urinary: 'Urinary / renal',
  renal: 'Renal',
  dermatology: 'Skin / allergy',
  eye: 'Eye',
  ent: 'ENT / hearing',
  reproductive: 'Reproductive health',
  maternal: 'Maternal care',
  newborn: 'Newborn',
  paediatric: 'Child health',
  adolescent: 'Adolescent health',
  geriatric: 'Geriatric',
  ncd: 'NCD / chronic care',
  metabolic: 'Metabolic',
  occupational: 'Occupational / work-exposure',
  injury: 'Injury / trauma',
  wound: 'Wound care',
  'mental-health': 'Mental health / psychosocial',
  substance: 'Substance-use',
  'medication-safety': 'Medication safety',
  'red-flag': 'Red-flag review',
  dehydration: 'Hydration / dehydration',
  nutrition: 'Nutrition',
  procedure: 'Procedure',
  counseling: 'Counselling',
  'follow-up': 'Follow-up',
  development: 'Development',
  immunization: 'Immunization',
  infection: 'Infection',
  toxicology: 'Toxic exposure',
  heat: 'Heat illness',
  constitutional: 'Constitutional symptoms',
  general: 'General clinical review',
});

const MODULE_SECTION_PLAN = Object.freeze({
  'fever-infection': {
    examSystems: ['general', 'respiratory'],
    investigationBlocks: ['cbc', 'orders'],
    medicineCategories: ['Infectious'],
    prompts: {
      history: [
        'Characterise onset, duration, associated symptoms, exposures and progression.',
        'Screen for red flags and hydration concerns before routine documentation.',
      ],
      examination: [
        'Prioritise temperature, pulse, respiratory rate, oxygen saturation and general condition.',
        'Add focused system examination according to the associated complaints rather than duplicating separate pathways.',
      ],
      investigations: [
        'Use severity, duration, examination findings and the active programme/rule pack to decide whether testing is needed.',
        'Do not auto-order a broad panel merely because fever is present.',
      ],
      prescription: [
        'Confirm the working diagnosis before choosing antimicrobial or symptomatic treatment.',
        'Check allergy, age/weight and other safety context before medicine selection.',
      ],
    },
  },
  respiratory: {
    examSystems: ['respiratory'],
    investigationBlocks: ['imaging', 'orders'],
    medicineCategories: ['Respiratory'],
    prompts: {
      history: ['Clarify cough/breathlessness/wheeze pattern, severity, triggers, exposures and red flags.'],
      examination: ['Prioritise respiratory rate, oxygen saturation and focused respiratory examination.'],
      investigations: ['Use respiratory findings and severity to decide whether imaging or other tests are indicated.'],
      prescription: ['Review inhaled/oral treatment suitability, technique, adherence and safety where relevant.'],
    },
  },
  cardiovascular: {
    examSystems: ['cardiovascular'],
    investigationBlocks: ['sugar', 'orders'],
    medicineCategories: ['Non‑communicable'],
    prompts: {
      history: ['Clarify exertional symptoms, chest discomfort, palpitations, syncope and cardiovascular risk context.'],
      examination: ['Prioritise blood pressure, pulse and focused cardiovascular examination.'],
      investigations: ['Choose cardiovascular/metabolic testing according to the working diagnosis and risk profile.'],
      prescription: ['Review blood pressure, comorbidity, contraindications and adherence before changing chronic therapy.'],
    },
  },
  gastrointestinal: {
    examSystems: ['gastrointestinal'],
    investigationBlocks: ['orders', 'other'],
    medicineCategories: ['Gastrointestinal'],
    prompts: {
      history: ['Characterise pain, vomiting, stool pattern, bleeding, hydration and food/medication context.'],
      examination: ['Prioritise hydration/general condition and focused abdominal examination.'],
      investigations: ['Select laboratory or imaging tests only when supported by the clinical pattern or severity.'],
      prescription: ['Match symptomatic treatment to the working diagnosis and review hydration/follow-up needs.'],
    },
  },
  dehydration: {
    examSystems: ['general'],
    investigationBlocks: ['orders'],
    prompts: {
      history: ['Clarify intake, losses, urine output and duration.'],
      examination: ['Prioritise hydration status and haemodynamic observations.'],
      investigations: ['Use clinical severity to decide whether laboratory testing is needed.'],
      prescription: ['Ensure the management plan explicitly addresses fluid replacement and escalation when required.'],
    },
  },
  neurological: {
    examSystems: ['neurological'],
    investigationBlocks: ['orders', 'other'],
    medicineCategories: ['Neurology/Psychiatry'],
    prompts: {
      history: ['Clarify onset, progression, focal symptoms, seizures, consciousness change and medication/substance context.'],
      examination: ['Prioritise mental status and focused neurological examination when indicated.'],
      investigations: ['Select investigations according to focal findings, red flags and the working diagnosis.'],
      prescription: ['Review sedation risk, interactions and follow-up needs when neurological medicines are considered.'],
    },
  },
  musculoskeletal: {
    examSystems: ['musculoskeletal'],
    investigationBlocks: ['orders', 'other'],
    medicineCategories: ['Musculoskeletal'],
    prompts: {
      history: ['Clarify site, mechanism, function, activity/work relationship and red flags.'],
      examination: ['Prioritise site-specific movement, tenderness, neurovascular status and functional impact.'],
      investigations: ['Use injury pattern, red flags and examination findings to decide whether imaging is required.'],
      prescription: ['Consider analgesic safety, functional advice and follow-up rather than medicine alone.'],
    },
  },
  urinary: {
    examSystems: ['urinary'],
    investigationBlocks: ['orders', 'other'],
    prompts: {
      history: ['Clarify urinary symptoms, fever, flank pain, pregnancy context and urine-output change.'],
      examination: ['Prioritise hydration, abdominal/flank findings and systemic illness.'],
      investigations: ['Use symptoms and risk context to decide on urine, renal or other testing.'],
      prescription: ['Review renal function, pregnancy context and antimicrobial indication where relevant.'],
    },
  },
  renal: {
    examSystems: ['urinary', 'cardiovascular'],
    investigationBlocks: ['orders', 'other'],
    prompts: {
      examination: ['Prioritise volume status, blood pressure and relevant renal/systemic findings.'],
      investigations: ['Use the working diagnosis to choose renal-function, urine or electrolyte assessment.'],
      prescription: ['Check renal safety and dose adjustment requirements before prescribing relevant medicines.'],
    },
  },
  dermatology: {
    examSystems: ['skin'],
    investigationBlocks: ['other'],
    medicineCategories: ['Dermatology'],
    prompts: {
      history: ['Clarify distribution, onset, contact/work exposures, medicines and systemic symptoms.'],
      examination: ['Describe lesion morphology/distribution and inspect relevant exposed areas.'],
      investigations: ['Most skin presentations are clinically assessed; add tests only when indicated by the differential.'],
      prescription: ['Match topical/systemic therapy to the working diagnosis and review exposure avoidance when relevant.'],
    },
  },
  eye: {
    examSystems: ['eyes'],
    investigationBlocks: ['other'],
    prompts: {
      history: ['Clarify pain, visual change, redness/discharge, trauma and exposure history.'],
      examination: ['Prioritise visual function and focused eye examination within available scope.'],
      investigations: ['Use referral or targeted testing rather than routine broad investigation when red flags are present.'],
      prescription: ['Avoid treatment that could delay urgent eye referral when visual or other red flags are present.'],
    },
  },
  ent: {
    examSystems: ['ent'],
    investigationBlocks: ['other'],
    prompts: {
      history: ['Clarify hearing/ear/nasal/throat symptoms, duration and occupational noise or irritant exposure.'],
      examination: ['Prioritise focused ENT examination and hearing-related assessment when relevant.'],
      investigations: ['Use targeted ENT/hearing testing or referral according to the findings.'],
      prescription: ['Match treatment and follow-up to the focused ENT working diagnosis.'],
    },
  },
  reproductive: {
    examSystems: ['reproductive'],
    investigationBlocks: ['orders', 'other'],
    prompts: {
      history: ['Use respectful, relevant reproductive/sexual history and screen for urgent symptoms.'],
      examination: ['Perform only indicated, consented examination with appropriate privacy/chaperone practice.'],
      investigations: ['Choose reproductive/STI/pregnancy-related tests according to the clinical context.'],
      prescription: ['Review pregnancy possibility, contraindications, counselling and follow-up before treatment.'],
    },
  },
  maternal: {
    examSystems: ['obstetric', 'general'],
    investigationBlocks: ['cbc', 'sugar', 'orders'],
    medicineCategories: ['Pregnancy'],
    prompts: {
      history: ['Keep maternal danger-sign and gestational/postnatal context visible throughout the Visit.'],
      examination: ['Prioritise maternal observations and the appropriate obstetric/postnatal examination for this service pathway.'],
      investigations: ['Use the active maternal-care rule pack to determine due/indicated tests and avoid duplicating tests already documented.'],
      prescription: ['Apply pregnancy/lactation medicine-safety checks and the active maternal-care rule pack before prescribing.'],
    },
  },
  newborn: {
    examSystems: ['general', 'respiratory'],
    investigationBlocks: ['orders'],
    medicineCategories: ['Paediatric'],
    prompts: {
      history: ['Prioritise feeding, activity, temperature, breathing, jaundice and caregiver-reported danger signs.'],
      examination: ['Use newborn/young-infant observations and focused examination rather than adult defaults.'],
      investigations: ['Use the active newborn/young-infant rule pack to decide whether targeted testing or referral is required.'],
      prescription: ['Use age/weight-specific treatment rules only when a reviewed paediatric rule is active.'],
    },
  },
  paediatric: {
    examSystems: ['general', 'respiratory', 'gastrointestinal'],
    investigationBlocks: ['cbc', 'orders'],
    medicineCategories: ['Paediatric'],
    prompts: {
      history: ['Keep age, feeding, immunization, growth and caregiver-reported danger signs in context.'],
      examination: ['Use age-appropriate observations, growth/anthropometry and focused system examination.'],
      investigations: ['Use the active child-health rule pack and clinical severity rather than routine adult panels.'],
      prescription: ['Prefer reviewed age/weight-specific dose rules; do not derive dose automatically from free-text references.'],
    },
  },
  adolescent: {
    examSystems: ['general'],
    investigationBlocks: ['orders'],
    prompts: {
      history: ['Use developmentally appropriate, confidential history including psychosocial context when relevant.'],
      examination: ['Keep examination focused on the presenting problem and age-appropriate risk context.'],
      prescription: ['Review age, pregnancy possibility where relevant, adherence and confidentiality considerations.'],
    },
  },
  geriatric: {
    examSystems: ['general', 'cardiovascular', 'neurological'],
    investigationBlocks: ['sugar', 'orders'],
    medicineCategories: ['Non‑communicable'],
    prompts: {
      history: ['Include function, falls, cognition, medicines and caregiver/support context where relevant.'],
      examination: ['Prioritise functional status, gait/falls risk and focused examination for active complaints/comorbidities.'],
      prescription: ['Review polypharmacy, adherence, renal/hepatic context and fall/sedation risk before medicine changes.'],
    },
  },
  ncd: {
    examSystems: ['cardiovascular', 'general'],
    investigationBlocks: ['sugar', 'orders'],
    medicineCategories: ['Non‑communicable'],
    prompts: {
      history: ['Review diagnosis control, adherence, lifestyle, complications and interval events.'],
      examination: ['Prioritise blood pressure, weight/BMI and complication-focused examination appropriate to the NCD.'],
      investigations: ['Use the relevant programme/rule pack to identify due monitoring tests and avoid unnecessary repetition.'],
      prescription: ['Check adherence, contraindications and monitoring results before intensifying chronic therapy.'],
    },
  },
  metabolic: {
    examSystems: ['general'],
    investigationBlocks: ['sugar', 'orders'],
    medicineCategories: ['Non‑communicable'],
  },
  occupational: {
    examSystems: ['general'],
    investigationBlocks: ['orders', 'other'],
    prompts: {
      history: ['Document the actual work performed, relevant exposure and work-symptom relationship; expand to focused occupational history when indicated.'],
      examination: ['Focus examination on the suspected target organ/system while keeping exposure context visible.'],
      investigations: ['Choose exposure- and organ-specific investigations only when supported by the occupational/clinical assessment.'],
      prescription: ['Include exposure-control, work advice and referral/follow-up when clinically indicated; treatment remains diagnosis-specific.'],
    },
  },
  injury: {
    examSystems: ['general', 'musculoskeletal'],
    investigationBlocks: ['imaging', 'orders'],
    medicineCategories: ['Musculoskeletal'],
    prompts: {
      history: ['Clarify mechanism, timing, contamination/exposure and immediate red flags.'],
      examination: ['Prioritise site assessment and neurovascular status before routine documentation.'],
      investigations: ['Use mechanism and examination findings to decide whether imaging or other testing is required.'],
      prescription: ['Document wound/procedure care, analgesia, prevention needs and follow-up as applicable.'],
    },
  },
  wound: {
    examSystems: ['skin'],
    investigationBlocks: ['other'],
    medicineCategories: ['Dermatology'],
  },
  'mental-health': {
    examSystems: ['neurological'],
    investigationBlocks: ['other'],
    medicineCategories: ['Neurology/Psychiatry'],
    prompts: {
      history: ['Prioritise safety, functional impact, substance use and psychosocial context when relevant.'],
      examination: ['Document mental-status/neurological findings appropriate to the presentation.'],
      investigations: ['Use targeted medical testing only when clinically indicated by the differential.'],
      prescription: ['Review safety, interactions, follow-up and escalation needs before medicine initiation or change.'],
    },
  },
  'medication-safety': {
    prompts: {
      history: ['Clarify current medicines, adherence, adverse effects and allergy history.'],
      prescription: ['Reconcile current medicines and review interactions, contraindications and monitoring before changes.'],
    },
  },
  'red-flag': {
    examSystems: ['general'],
    investigationBlocks: ['orders'],
    medicineCategories: ['Emergency'],
    prompts: {
      history: ['Do not delay urgent clinical assessment/escalation to complete routine history fields.'],
      examination: ['Prioritise immediate assessment and escalation before routine form completion.'],
      investigations: ['Urgent testing should follow the emergency assessment and available escalation pathway.'],
      prescription: ['Do not allow routine prescription workflow to delay stabilization, escalation or referral.'],
    },
  },
  nutrition: {
    examSystems: ['general'],
    investigationBlocks: ['orders'],
    prompts: {
      examination: ['Prioritise weight/growth and relevant anthropometry for the age/service context.'],
    },
  },
  procedure: {
    prompts: {
      prescription: ['Record the clinical procedure in the Visit once; any linked training Activity remains a separate educational record.'],
    },
  },
  counseling: {
    prompts: {
      prescription: ['Record counselling delivered in the Visit once; linked training evidence remains separate.'],
    },
  },
  'follow-up': {
    prompts: {
      prescription: ['Make follow-up timing, mode, reason and warning advice explicit when follow-up is required.'],
    },
  },
  toxicology: {
    examSystems: ['general', 'neurological'],
    investigationBlocks: ['orders'],
    medicineCategories: ['Emergency'],
  },
  heat: {
    examSystems: ['general'],
    investigationBlocks: ['orders'],
    medicineCategories: ['Emergency'],
  },
});

export function moduleLabels(modules = []) {
  return Array.from(new Set((modules || []).filter(Boolean)))
    .map((key) => ({ key, label: CLINICAL_MODULE_LABELS[key] || key }));
}

function dedupe(values = []) {
  return Array.from(new Set(values.filter(Boolean)));
}

export function getSectionModulePlan({ modules = [], section = 'history' } = {}) {
  const keys = dedupe(modules);
  const examSystems = [];
  const investigationBlocks = [];
  const medicineCategories = [];
  const prompts = [];

  keys.forEach((key) => {
    const config = MODULE_SECTION_PLAN[key] || {};
    examSystems.push(...(config.examSystems || []));
    investigationBlocks.push(...(config.investigationBlocks || []));
    medicineCategories.push(...(config.medicineCategories || []));
    prompts.push(...(config.prompts?.[section] || []));
  });

  return {
    modules: keys,
    examSystems: dedupe(examSystems),
    investigationBlocks: dedupe(investigationBlocks),
    medicineCategories: dedupe(medicineCategories),
    prompts: dedupe(prompts),
  };
}
